import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Heart, Sparkles, Sun, Moon, Compass, Flower2 } from 'lucide-react';
import EbookPageFooter from '../shared/EbookPageFooter';

export default function SelfEmpathy() {
  const navigate = useNavigate();

  const steps = [
    {
      number: 1,
      title: 'Notice your judgments',
      question: 'What am I telling myself about this situation or person?',
      icon: Moon,
      color: 'from-slate-800 to-slate-700',
      accentColor: 'text-slate-300'
    },
    {
      number: 2,
      title: 'Connect with your feelings',
      question: 'How am I feeling right now?',
      icon: Heart,
      color: 'from-rose-900/30 to-rose-800/20',
      accentColor: 'text-rose-300'
    },
    {
      number: 3,
      title: 'Identify your needs',
      question: 'What do I need in this moment?',
      icon: Sun,
      color: 'from-amber-900/30 to-amber-800/20',
      accentColor: 'text-amber-300'
    },
    {
      number: 4,
      title: 'Make a request to yourself',
      question: 'Where do I want to focus my attention now?',
      icon: Compass,
      color: 'from-teal-900/30 to-teal-800/20',
      accentColor: 'text-teal-300'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Decorative background elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 right-20 w-72 h-72 bg-rose-500/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 left-20 w-80 h-80 bg-amber-500/5 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-teal-500/3 rounded-full blur-3xl"></div>
      </div>

      {/* Header */}
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/belonging-ebook')}
                variant="outline"
                size="sm"
                className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Self-Empathy</h1>
              </div>
            </div>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <BookOpen className="h-4 w-4" />
              <span>Page 70</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-6 py-12 relative z-10">
        <div className="max-w-4xl mx-auto">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-rose-500/10 border border-rose-500/20 rounded-full text-rose-300 text-sm mb-6">
              <Flower2 className="h-4 w-4" />
              <span>Inner Connection Practice</span>
            </div>
            
            <div className="relative inline-block mb-8">
              <div className="absolute inset-0 bg-gradient-to-r from-rose-500/20 to-amber-500/20 rounded-full blur-2xl"></div>
              <Heart className="h-24 w-24 text-rose-400 relative animate-pulse" style={{ animationDuration: '3s' }} />
            </div>
            
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
              Compassionate Attention<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-amber-400">to Ourselves</span>
            </h2>
            <p className="text-xl text-slate-300 leading-relaxed max-w-2xl mx-auto">
              Self-empathy is bringing the same compassionate attention to ourselves that we give to others 
              when listening using NVC.
            </p>
          </div>

          {/* Inspirational Quote */}
          <div className="bg-gradient-to-r from-rose-900/20 to-amber-900/20 border border-rose-500/20 rounded-2xl p-8 mb-12 text-center relative overflow-hidden">
            <div className="absolute top-0 left-0 w-24 h-24 bg-rose-500/10 rounded-full blur-2xl"></div>
            <div className="absolute bottom-0 right-0 w-32 h-32 bg-amber-500/10 rounded-full blur-2xl"></div>
            <Sparkles className="h-8 w-8 text-amber-400 mx-auto mb-4" />
            <p className="text-xl text-slate-200 italic leading-relaxed relative">
              "Self-empathy at times like this has the power to transform our disconnected state of being 
              and return us to our compassionate intention."
            </p>
          </div>

          {/* Main Content Sections */}
          <div className="space-y-8 mb-16">
            {/* What is Self-Empathy */}
            <div className="bg-gradient-to-br from-rose-900/20 to-rose-800/10 border border-rose-700/30 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 opacity-10">
                <Heart className="h-32 w-32 text-rose-300" />
              </div>
              <div className="relative">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="p-2 bg-rose-500/20 rounded-lg">
                    <Heart className="h-5 w-5 text-rose-400" />
                  </span>
                  What is Self-Empathy?
                </h3>
                <p className="text-slate-200 leading-relaxed mb-4">
                  In self-empathy, we bring the same compassionate attention to ourselves that we give to others 
                  when listening to them using NVC. This means listening through any interpretations and judgments 
                  of ourselves that we are making in order to clarify how we are feeling and what we are needing.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  This inner awareness and clarity supports us in expressing ourselves to others, or receiving them 
                  with empathy. It allows us to make a request to ourselves about where we want to focus our attention.
                </p>
              </div>
            </div>

            {/* The Practice */}
            <div className="bg-gradient-to-br from-teal-900/20 to-teal-800/10 border border-teal-700/30 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 opacity-10">
                <Compass className="h-32 w-32 text-teal-300" />
              </div>
              <div className="relative">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="p-2 bg-teal-500/20 rounded-lg">
                    <Compass className="h-5 w-5 text-teal-400" />
                  </span>
                  The Practice of NVC
                </h3>
                <p className="text-slate-200 leading-relaxed mb-4">
                  The practice of NVC entails an intention to connect compassionately both with ourselves and with others, 
                  and an ability to keep our attention in the present moment—which includes being aware that sometimes 
                  in this present moment we are recalling the past, or imagining a future possibility.
                </p>
                <p className="text-slate-300 leading-relaxed">
                  Often self-empathy comes easy, as we access our sensations, emotions and needs, to attune to how we are. 
                  However, in moments of conflict or reactivity to others, we may find ourselves reluctant to access an 
                  intention to connect compassionately.
                </p>
              </div>
            </div>

            {/* Transformative Power */}
            <div className="bg-gradient-to-br from-amber-900/20 to-amber-800/10 border border-amber-700/30 rounded-2xl p-8 relative overflow-hidden">
              <div className="absolute top-4 right-4 opacity-10">
                <Sun className="h-32 w-32 text-amber-300" />
              </div>
              <div className="relative">
                <h3 className="text-2xl font-bold text-white mb-4 flex items-center gap-3">
                  <span className="p-2 bg-amber-500/20 rounded-lg">
                    <Sun className="h-5 w-5 text-amber-400" />
                  </span>
                  The Transformative Power
                </h3>
                <p className="text-slate-200 leading-relaxed mb-4">
                  Self-empathy has the power to transform our disconnected state of being and 
                  return us to our compassionate intention and present-oriented attention.
                </p>
                <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 mt-4">
                  <p className="text-amber-200 leading-relaxed font-medium">
                    With practice, many people find that self-empathy alone sometimes resolves inner conflicts 
                    and conflicts with others as it transforms our experience of life.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Self-Empathy Process - Enhanced */}
          <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-white mb-2">Self-Empathy Process</h3>
              <p className="text-slate-400">Four gentle steps to reconnect with yourself</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {steps.map((step) => {
                const IconComponent = step.icon;
                return (
                  <div key={step.number} className={`bg-gradient-to-br ${step.color} border border-slate-600/30 rounded-xl p-6 hover:scale-[1.02] transition-all duration-300`}>
                    <div className="flex items-start gap-4">
                      <div className={`p-3 bg-black/20 rounded-xl ${step.accentColor}`}>
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-white/40 text-sm font-mono">Step {step.number}</span>
                        </div>
                        <p className="text-white font-semibold mb-2">{step.title}</p>
                        <p className={`text-sm ${step.accentColor}`}>{step.question}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Closing */}
          <div className="mt-12 text-center">
            <div className="bg-gradient-to-r from-slate-800/30 to-slate-700/20 border border-slate-600/20 rounded-2xl p-8">
              <Flower2 className="h-10 w-10 text-rose-400 mx-auto mb-4" />
              <p className="text-lg text-slate-300 leading-relaxed max-w-2xl mx-auto">
                Be gentle with yourself. Self-empathy is a practice of tenderness, 
                not another way to judge ourselves.
              </p>
            </div>
          </div>

          {/* Practice Scenario & Contact Form */}
          <EbookPageFooter 
            pageContext="Self-Empathy Fundamentals"
            pageNumber={145}
            showScenario={true}
            showContactForm={true}
          />
        </div>
      </div>
    </div>
  );
}
